import React, { useState, useRef } from 'react';
import { Truck, User, Building2, Shield, LogIn, RefreshCw, Eye, EyeOff, ArrowLeft, CheckCircle, Upload, FileText, AlertCircle, ChevronRight, X, MapPin, Package } from 'lucide-react';
import { login, setSesion, resetStore, registrarTransportista, registrarCliente, getStore, DOCUMENTOS_REQUERIDOS, vincularReferidoEmpresa, vincularPersonaFisicaRed } from './store';
import { apiLogin, apiRegistrarTransportista, apiRegistrarClienteCompleto, apiCheckEmail, apiForgotPassword, apiResetPassword } from './api';
import { phCapture } from './posthog';
import { validarDigitoVerificadorRFC } from './utils/rfc';

// ========== CIUDADES BAJA CALIFORNIA ==========
const CIUDADES_BC = [
  // Baja California (BC) — Zona Tijuana/Rosarito
  'Tijuana, BC', 'Rosarito, BC', 'Playas de Rosarito, BC', 'La Misión, BC',
  'El Descanso, BC', 'Primo Tapia, BC', 'Popotla, BC', 'Puerto Nuevo, BC',
  // BC — Ensenada y costa noroeste
  'Ensenada, BC', 'Maneadero, BC', 'El Sauzal, BC', 'Punta Banda, BC',
  'Santo Tomás, BC', 'Eréndira, BC', 'San Quintín, BC', 'Camalu, BC',
  'Colonet, BC', 'San Vicente, BC', 'El Rosario, BC', 'Vicente Guerrero, BC',
  'Valle de Guadalupe, BC',
  // BC — Tecate y sierra
  'Tecate, BC', 'El Hongo, BC', 'La Rumorosa, BC', 'Valle de las Palmas, BC',
  'Ojos Negros, BC',
  // BC — Mexicali y valle
  'Mexicali, BC', 'Algodones, BC', 'San Felipe, BC', 'Guadalupe Victoria, BC',
  'Ejido Nuevo León, BC', 'Ejido Sonora, BC', 'Ciudad Morelos, BC',
  // BC — Bahía de los Ángeles / costa del golfo
  'Bahía de los Ángeles, BC', 'El Mármol, BC', 'Punta Prieta, BC',
  // Baja California Sur (BCS) — Frontera norte BCS
  'Guerrero Negro, BCS', 'Bahía Asunción, BCS', 'Punta Abreojos, BCS',
  'San Ignacio, BCS', 'Vizcaíno, BCS', 'Villa Alberto Andrés Alvarado, BCS',
  // BCS — Santa Rosalía / Mulegé
  'Santa Rosalía, BCS', 'Mulegé, BCS', 'San Carlos, BCS',
  'Bahía Concepción, BCS', 'Loreto, BCS', 'Puerto Escondido, BCS',
  // BCS — La Paz y alrededores
  'La Paz, BCS', 'El Centenario, BCS', 'La Ventana, BCS', 'Los Planes, BCS',
  'Todos Santos, BCS', 'El Pescadero, BCS', 'Ciudad Constitución, BCS',
  'Ciudad Insurgentes, BCS', 'Comondú, BCS',
  // BCS — Los Cabos y Corredor
  'Cabo San Lucas, BCS', 'San José del Cabo, BCS', 'Los Barriles, BCS',
  'Buena Vista, BCS', 'La Ribera, BCS', 'Miraflores, BCS', 'Santiago, BCS',
  'El Triunfo, BCS', 'San Antonio, BCS',
];

// ========== MODAL TÉRMINOS Y CONDICIONES ==========
function ModalTerminos({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800">Términos y Condiciones — Choyero Marketplace Logístico</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none">×</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 text-sm text-gray-700 space-y-5 flex-1">
          <p className="text-xs text-gray-400">Versión 2026-04 · Última actualización: abril 2026</p>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">1. Objeto y alcance</h3>
            <p>Choyero Marketplace Logístico (en adelante "Choyero" o "la Plataforma") es un servicio digital que conecta a personas físicas y morales que requieren servicios de transporte de carga (Clientes) con empresas transportistas y sus choferes (Transportistas) dentro de la Península de Baja California, México. El uso de la Plataforma implica la aceptación total de estos Términos y Condiciones.</p>
            <p className="mt-1">El uso de la Plataforma está reservado a personas mayores de 18 años con plena capacidad jurídica para contratar conforme a la legislación mexicana.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">2. Registro y cuentas</h3>
            <p>Para usar Choyero debes registrarte con información verídica y actualizada. Eres responsable de mantener la confidencialidad de tu contraseña. Choyero se reserva el derecho de suspender o cancelar cuentas que proporcionen información falsa o que incumplan estos términos.</p>
            <p className="mt-1">Las empresas transportistas deberán subir la documentación requerida (licencias, permisos SCT, pólizas de seguro, etc.) y ser aprobadas por el equipo de Choyero antes de operar en la plataforma.</p>
            <p className="mt-1">La fecha y versión de aceptación de estos Términos quedan registradas en nuestros sistemas como constancia del consentimiento otorgado.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">3. Roles y responsabilidades</h3>
            <p><span className="font-semibold">Cliente:</span> Es responsable de proporcionar información precisa sobre la carga (tipo, peso, volumen, origen y destino), asegurar que la carga esté lista en el punto de recogida y confirmar la entrega una vez recibida.</p>
            <p className="mt-1"><span className="font-semibold">Transportista:</span> Es responsable de contar con los permisos y seguros vigentes exigidos por la SCT y la ley mexicana, ejecutar el servicio en los términos pactados, y designar choferes certificados y con licencia vigente.</p>
            <p className="mt-1"><span className="font-semibold">Chofer:</span> Deberá portar licencia federal vigente, respetar las normativas de tránsito, y mantener comunicación con el Transportista durante el servicio.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">4. Pagos, escrow y comisiones</h3>
            <p>El precio del servicio es acordado entre el Cliente y el Transportista dentro de la Plataforma. Choyero cobra una comisión de intermediación sobre el monto pactado, cuyo porcentaje vigente se muestra en la Plataforma antes de confirmar cada operación. La comisión se descuenta automáticamente al liberar el pago al Transportista.</p>
            <p className="mt-2 font-semibold text-gray-800">Mecanismo de pago en garantía (escrow):</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li>Al aceptar una cotización, el Cliente realiza el pago completo del servicio a través de los métodos habilitados en la Plataforma (tarjeta, transferencia, etc.).</li>
              <li>Los fondos quedan retenidos por Choyero a través del procesador de pagos autorizado (MercadoPago) y <span className="font-semibold">no se liberan al Transportista</span> hasta que el servicio sea completado.</li>
              <li>El pago se libera automáticamente al Transportista una vez que el Cliente confirma la entrega correcta de la carga en la Plataforma.</li>
              <li>Si el Cliente no confirma ni reporta incidencia dentro de las <span className="font-semibold">72 horas</span> posteriores a la fecha estimada de entrega, el pago se libera automáticamente al Transportista.</li>
              <li>La comisión de Choyero se descuenta del monto total antes de realizar la transferencia al Transportista.</li>
            </ul>
            <p className="mt-2 font-semibold text-gray-800">Retenciones fiscales:</p>
            <p>Choyero emitirá el comprobante fiscal (CFDI) correspondiente a la comisión cobrada. En caso de que el Transportista sea persona física con obligaciones de retención de ISR e IVA conforme a la legislación mexicana vigente, Choyero aplicará las retenciones que procedan conforme a la ley.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">5. Retornos vacíos</h3>
            <p>La funcionalidad de Fletes de Retorno permite a los Transportistas ofrecer espacio disponible en viajes de regreso. Las ofertas, contraofertas y precios mínimos son gestionados directamente entre las partes. Choyero no garantiza la disponibilidad de retornos ni el cierre de acuerdos.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">6. Cancelaciones, reembolsos y disputas</h3>
            <p className="font-semibold text-gray-800">Cancelaciones:</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li><span className="font-semibold">Antes de que el chofer inicie el viaje:</span> el Cliente puede cancelar y recibir reembolso completo.</li>
              <li><span className="font-semibold">Una vez iniciado el viaje:</span> no procede reembolso salvo incumplimiento comprobable del Transportista.</li>
              <li><span className="font-semibold">Cancelación por el Transportista:</span> los fondos retenidos se reembolsan íntegramente al Cliente.</li>
            </ul>
            <p className="mt-2 font-semibold text-gray-800">Disputas:</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li>Cualquiera de las partes puede abrir una disputa desde la Plataforma dentro de las 72 horas posteriores a la fecha de entrega.</li>
              <li>Choyero actuará como mediador y podrá retener los fondos hasta <span className="font-semibold">72 horas hábiles</span> adicionales para resolver la incidencia.</li>
              <li>La resolución de Choyero se basará en la evidencia disponible en la Plataforma (fotos, mensajes, confirmaciones).</li>
              <li>Choyero se reserva el derecho de liberar los fondos al Transportista, reembolsar al Cliente, o aplicar una división proporcional según el caso.</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">7. Programa de referidos y comisiones MLM</h3>
            <p>Choyero cuenta con un sistema de referidos que permite a los usuarios ganar comisiones cuando las personas que invitan generan actividad en la Plataforma. Al participar en este programa, el usuario acepta expresamente:</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li>Las comisiones generadas están sujetas a retención de ISR conforme al régimen de <span className="font-semibold">Asimilados a Salarios</span> y a las tablas del SAT vigentes.</li>
              <li>Choyero emitirá el CFDI correspondiente por cada pago de comisión realizado.</li>
              <li>Los retiros de comisiones están sujetos a los límites mínimos, máximos y periodicidad establecidos en la Plataforma.</li>
              <li>Queda prohibido manipular el sistema de referidos mediante cuentas falsas, referidos ficticios o cualquier mecanismo de fraude. Choyero puede suspender cuentas y retener saldos ante indicios de abuso.</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">8. Conducta prohibida</h3>
            <p>Los usuarios no podrán, bajo ninguna circunstancia:</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li>Crear cuentas falsas o suplantar la identidad de terceros.</li>
              <li>Acordar servicios fuera de la Plataforma con el fin de evadir la comisión de Choyero.</li>
              <li>Manipular calificaciones, reseñas o el sistema de reputación.</li>
              <li>Realizar scraping, extracción automatizada de datos o ingeniería inversa sobre la Plataforma.</li>
              <li>Usar la información obtenida en la Plataforma para desarrollar o comercializar un servicio competidor.</li>
              <li>Introducir virus, código malicioso o cualquier elemento que afecte el funcionamiento de la Plataforma.</li>
              <li>Realizar transacciones fraudulentas o intentar procesar pagos con medios no autorizados.</li>
            </ul>
            <p className="mt-1">El incumplimiento de cualquiera de estos puntos puede resultar en la suspensión inmediata de la cuenta, retención de saldos pendientes y, en su caso, acciones legales.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">9. Limitación de responsabilidad</h3>
            <p>Choyero actúa como intermediario tecnológico y no es parte del contrato de transporte entre el Cliente y el Transportista. Choyero no se responsabiliza por daños, pérdidas o retrasos en la carga, ni por accidentes ocurridos durante el traslado. El Transportista es el único responsable de contar con el seguro de carga correspondiente.</p>
            <p className="mt-1">La Plataforma se ofrece "tal como está". Choyero no garantiza disponibilidad ininterrumpida del servicio y puede realizar mantenimientos programados sin previo aviso, sin que esto genere responsabilidad alguna. La Plataforma utiliza cookies y almacenamiento local (localStorage) para gestionar la sesión del usuario y mejorar la experiencia de uso.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">10. Protección de datos personales (Aviso de Privacidad)</h3>
            <p className="font-semibold text-gray-800">Responsable del tratamiento:</p>
            <p>Choyero Marketplace Logístico, con domicilio en Baja California, México. Contacto: <span className="font-semibold text-orange-600">legal@choyero.app</span></p>
            <p className="mt-2 font-semibold text-gray-800">Datos que recabamos:</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li><span className="font-semibold">Identificación:</span> nombre, email, teléfono, RFC, CURP, CLABE bancaria.</li>
              <li><span className="font-semibold">Operación:</span> origen y destino de fletes, historial de servicios, calificaciones.</li>
              <li><span className="font-semibold">Fiscales:</span> datos para emisión de CFDI (régimen, CP fiscal, uso CFDI, Constancia de Situación Fiscal).</li>
              <li><span className="font-semibold">Técnicos:</span> dirección IP, dispositivo, sesiones de uso.</li>
            </ul>
            <p className="mt-2 font-semibold text-gray-800">Finalidades:</p>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              <li>Prestación del servicio de intermediación logística.</li>
              <li>Procesamiento de pagos y cumplimiento de obligaciones fiscales.</li>
              <li>Comunicaciones relacionadas con el servicio (notificaciones, alertas, soporte).</li>
              <li>Prevención de fraude y cumplimiento legal.</li>
            </ul>
            <p className="mt-2 font-semibold text-gray-800">Transferencias:</p>
            <p>Tus datos pueden ser compartidos con MercadoPago (procesamiento de pagos), Facturama (timbrado CFDI) y autoridades competentes cuando la ley lo exija. No se ceden a terceros con fines comerciales.</p>
            <p className="mt-2 font-semibold text-gray-800">Derechos ARCO:</p>
            <p>Puedes ejercer tus derechos de Acceso, Rectificación, Cancelación u Oposición enviando un correo a <span className="font-semibold text-orange-600">legal@choyero.app</span> con identificación oficial. Choyero responderá en un plazo máximo de 20 días hábiles conforme a la LFPDPPP.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">11. Propiedad intelectual</h3>
            <p>Todos los elementos de la Plataforma — incluyendo código fuente, diseño, interfaz, marca, logotipos, algoritmos, flujos de negocio y bases de datos — son propiedad exclusiva de Choyero o sus licenciantes y están protegidos por las leyes de derechos de autor, marcas y demás normativa de propiedad intelectual aplicable en México.</p>
            <p className="mt-1">Queda expresamente prohibido: reproducir, copiar, distribuir, modificar, realizar ingeniería inversa, extraer datos mediante scraping o medios automatizados, y usar cualquier elemento de la Plataforma para desarrollar o comercializar servicios iguales o similares, sin autorización escrita de Choyero.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">12. Modificaciones</h3>
            <p>Choyero se reserva el derecho de modificar estos Términos en cualquier momento. Los cambios serán notificados a través de la Plataforma indicando la nueva versión y fecha. El uso continuado del servicio tras la notificación implica la aceptación de los nuevos términos.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">13. Jurisdicción y legislación aplicable</h3>
            <p>Estos Términos se rigen por las leyes vigentes de los Estados Unidos Mexicanos. Para cualquier controversia, las partes se someten a la jurisdicción de los tribunales competentes del estado de Baja California, México, renunciando a cualquier otro fuero que pudiera corresponderles.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-1">14. Contacto</h3>
            <p>Para dudas o aclaraciones sobre estos Términos o el Aviso de Privacidad, puedes contactarnos a través del correo electrónico: <span className="font-semibold text-orange-600">legal@choyero.app</span></p>
          </section>
        </div>
        <div className="px-6 py-4 border-t flex-shrink-0 text-right">
          <button onClick={onClose} className="px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-semibold text-sm">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

// ========== COMBOBOX DE CIUDADES ==========
function CiudadCombobox({ placeholder, value, onChange, ringColor = 'focus:ring-orange-400', dark = false }) {
  const [abierto, setAbierto] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const listaRef = useRef(null);
  const filtrar = (texto) =>
    texto ? CIUDADES_BC.filter(c => c.toLowerCase().includes(texto.toLowerCase())) : CIUDADES_BC;

  const opciones = filtrar(value);

  const seleccionar = (ciudad) => { onChange(ciudad); setAbierto(false); setCursor(-1); };

  const handleKeyDown = (e) => {
    if (!abierto) { setAbierto(true); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(prev => {
        const next = prev < opciones.length - 1 ? prev + 1 : 0;
        setTimeout(() => listaRef.current?.children[next]?.scrollIntoView({ block: 'nearest' }), 0);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(prev => {
        const next = prev > 0 ? prev - 1 : opciones.length - 1;
        setTimeout(() => listaRef.current?.children[next]?.scrollIntoView({ block: 'nearest' }), 0);
        return next;
      });
    } else if (e.key === 'Enter') {
      if (cursor >= 0 && opciones[cursor]) { e.preventDefault(); seleccionar(opciones[cursor]); }
    } else if (e.key === 'Escape') {
      setAbierto(false); setCursor(-1);
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
        <MapPin size={15} className="text-gray-400" />
      </div>
      <input
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); setAbierto(true); setCursor(-1); }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => {
          setAbierto(false); setCursor(-1);
          if (value && !CIUDADES_BC.includes(value)) onChange('');
        }, 150)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className={dark
          ? `w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl ${ringColor} focus:ring-2 focus:outline-none text-white placeholder-gray-500 text-sm`
          : `w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl ${ringColor} focus:ring-2 focus:outline-none text-sm`}
      />
      {abierto && (
        <ul ref={listaRef} className={`absolute z-50 w-full rounded-xl shadow-lg bottom-full mb-1 max-h-52 overflow-y-auto ${dark ? 'bg-gray-800 border border-white/10' : 'bg-white border border-gray-200'}`}>
          {opciones.length === 0 ? (
            <li className={`px-4 py-3 text-sm italic ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Sin resultados</li>
          ) : opciones.map((ciudad, i) => (
            <li
              key={ciudad}
              onMouseDown={() => seleccionar(ciudad)}
              className={dark
                ? `px-4 py-2.5 text-sm cursor-pointer flex items-center gap-2 ${i === cursor ? 'bg-orange-500/20 text-orange-300' : value === ciudad ? 'font-semibold text-orange-400 bg-orange-500/10' : 'text-gray-300 hover:bg-white/10'}`
                : `px-4 py-2.5 text-sm cursor-pointer flex items-center gap-2 ${i === cursor ? 'bg-blue-50 text-blue-700 font-semibold' : value === ciudad ? 'font-semibold text-blue-700 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <MapPin size={12} className="text-gray-400 flex-shrink-0" />
              {ciudad}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ROLES_DEMO = [
  {
    email: 'cliente@demo.mx',
    password: '1234',
    label: 'Cliente',
    sublabel: 'María López',
    descripcion: 'Solicita fletes y compara cotizaciones',
    icon: User,
    color: 'blue',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    hover: 'hover:border-blue-400 hover:bg-blue-100',
    badge: 'bg-blue-600',
    iconColor: 'text-blue-600'
  },
  {
    email: 'chofer@demo.mx',
    password: '1234',
    label: 'Chofer',
    sublabel: 'Juan Pérez García',
    descripcion: 'Recibe y gestiona órdenes de trabajo',
    icon: Truck,
    color: 'green',
    bg: 'bg-green-50',
    border: 'border-green-200',
    hover: 'hover:border-green-400 hover:bg-green-100',
    badge: 'bg-green-600',
    iconColor: 'text-green-600'
  },
  {
    email: 'transportista@demo.mx',
    password: '1234',
    label: 'Transportista',
    sublabel: 'Transportes Veloz SA',
    descripcion: 'Cotiza solicitudes y gestiona tu flota',
    icon: Building2,
    color: 'orange',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    hover: 'hover:border-orange-400 hover:bg-orange-100',
    badge: 'bg-orange-600',
    iconColor: 'text-orange-600'
  },
  {
    email: 'admin@choyero.app',
    password: '1234',
    label: 'Super Admin',
    sublabel: 'Administrador Principal',
    descripcion: 'Administra empresas y la plataforma',
    icon: Shield,
    color: 'purple',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    hover: 'hover:border-purple-400 hover:bg-purple-100',
    badge: 'bg-purple-600',
    iconColor: 'text-purple-600'
  }
];

// Usar DOCUMENTOS_REQUERIDOS del store para garantizar nombres e IDs consistentes

function RegistroTransportista({ onVolver, onExito }) {
  const [paso, setPaso] = useState(1);
  const refUrlRaw = new URLSearchParams(window.location.search).get('ref');
  const refUrl = refUrlRaw && /^[A-Z0-9a-z_-]{3,20}$/.test(refUrlRaw) ? refUrlRaw : '';
  const [form, setForm] = useState({
    nombre: '', rfc: '', representante: '', emailContacto: '',
    telefono: '', calleNumero: '', ciudad: '', flota: '',
    email: '', password: '', confirmPassword: '', aceptaTerminos: false,
    codigoInvitacion: refUrl.toUpperCase(), codigoPromo: ''
  });
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [verTerminos, setVerTerminos] = useState(false);
  const [resultadoReferido, setResultadoReferido] = useState(null);

  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const validarPaso1 = () => {
    if (!form.nombre || !form.rfc || !form.representante || !form.telefono || !form.calleNumero || !form.ciudad || !form.flota) {
      setError('Completa todos los campos obligatorios');
      return false;
    }
    if (form.rfc.length !== 12 && form.rfc.length !== 13) {
      setError('El RFC debe tener 12 caracteres (persona moral) o 13 caracteres (persona física)');
      return false;
    }
    if (!validarDigitoVerificadorRFC(form.rfc)) {
      setError('El RFC no es válido. Verifica que esté escrito correctamente (dígito verificador incorrecto).');
      return false;
    }
    if (form.telefono.length !== 10) {
      setError('El teléfono debe tener exactamente 10 dígitos');
      return false;
    }
    setError('');
    return true;
  };

  const validarPaso3 = () => {
    if (!form.email || !form.password) {
      setError('Ingresa tu correo y contraseña de acceso');
      return false;
    }
    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    if (!form.aceptaTerminos) {
      setError('Debes aceptar los términos y condiciones');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validarPaso3()) return;
    setCargando(true);
    try {
      const disponible = await apiCheckEmail(form.email).catch(() => true);
      if (!disponible) {
        setError('Este correo ya está registrado en la plataforma');
        setCargando(false);
        return;
      }
      const tipoPersona = form.rfc.length === 13 ? 'fisica' : 'moral';
      await apiRegistrarTransportista(
        {
          nombre: form.nombre,
          rfc: form.rfc,
          representante: form.representante,
          emailContacto: form.emailContacto || form.email,
          telefono: form.telefono,
          ciudad: form.ciudad,
          direccion: form.calleNumero ? `${form.calleNumero}, ${form.ciudad}` : form.ciudad,
          flota: form.flota,
          tipoPersona,
        },
        { email: form.email, password: form.password },
        form.codigoInvitacion || undefined,
        form.codigoPromo || undefined
      );
      // También guardar en store local como fallback
      registrarTransportista({
        empresa: {
          nombre: form.nombre, rfc: form.rfc, representante: form.representante,
          emailContacto: form.emailContacto || form.email, telefono: form.telefono,
          ciudad: form.ciudad,
          direccion: form.calleNumero ? `${form.calleNumero}, ${form.ciudad}` : form.ciudad,
          flota: form.flota,
        },
        credenciales: { email: form.email, password: form.password }
      });
      phCapture('usuario_registro', { rol: 'transportista', ciudad: form.ciudad, flota: form.flota });
      setCargando(false);
      onExito();
    } catch (err) {
      setError(err.message || 'Error al registrar. Intenta de nuevo.');
      setCargando(false);
    }
  };

  const inputCls = 'w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-orange-400 focus:outline-none text-white placeholder-gray-500 text-sm';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 flex items-center justify-center p-4">
      {verTerminos && <ModalTerminos onClose={() => setVerTerminos(false)} />}
      <div className="w-full max-w-2xl">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onVolver} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition">
            <ArrowLeft size={16} /> Volver al inicio
          </button>
          <div className="flex items-center gap-3">
            <img src="/logo-choyero.png" alt="Choyero" className="h-12 w-auto" />
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          {/* Progress bar */}
          <div className="px-8 pt-6 pb-4 border-b border-white/10">
            <h2 className="text-2xl font-bold text-white mb-1">Registro de Transportista</h2>
            <p className="text-gray-400 text-sm mb-5">Completa los 2 pasos para enviar tu solicitud</p>
            <div className="flex items-center space-x-2">
              {[1, 2].map(p => (
                <React.Fragment key={p}>
                  <div className={`flex items-center space-x-2 ${paso >= p ? 'text-orange-400' : 'text-gray-500'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      paso > p ? 'bg-green-500 text-white' : paso === p ? 'bg-orange-500 text-white' : 'bg-white/10 text-gray-400'
                    }`}>
                      {paso > p ? <CheckCircle size={16} /> : p}
                    </div>
                    <span className="text-sm font-medium hidden sm:block">
                      {p === 1 ? 'Datos empresa' : 'Acceso'}
                    </span>
                  </div>
                  {p < 2 && <div className={`flex-1 h-1 rounded-full ${paso > p ? 'bg-green-500' : 'bg-white/10'}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="p-8">
            {/* ── PASO 1: Datos de la empresa ── */}
            {paso === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Nombre de la empresa *</label>
                    <input value={form.nombre} onChange={e => set('nombre', e.target.value.replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                      placeholder="Transportes del Norte SA de CV" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">RFC * <span className="text-gray-500 font-normal">(12 moral · 13 física)</span></label>
                    <input value={form.rfc}
                      onChange={e => set('rfc', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 13))}
                      placeholder="TDN850615XXX" maxLength={13}
                      className={`${inputCls} ${form.rfc.length > 0 && form.rfc.length !== 12 && form.rfc.length !== 13 ? '!border-red-500' : ''}`} />
                    {form.rfc.length > 0 && <p className="text-xs mt-1 text-right" style={{color: (form.rfc.length === 12 || form.rfc.length === 13) ? '#4ade80' : '#f87171'}}>{form.rfc.length}/12-13</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Nombre del titular *</label>
                    <input value={form.representante} onChange={e => set('representante', e.target.value.replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                      placeholder="Juan Pérez García" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Teléfono * <span className="text-gray-500 font-normal">(10 dígitos)</span></label>
                    <input value={form.telefono}
                      onChange={e => set('telefono', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="6641234567" maxLength={10} inputMode="numeric"
                      className={`${inputCls} ${form.telefono.length > 0 && form.telefono.length !== 10 ? '!border-red-500' : ''}`} />
                    {form.telefono.length > 0 && <p className="text-xs mt-1 text-right" style={{color: form.telefono.length === 10 ? '#4ade80' : '#f87171'}}>{form.telefono.length}/10</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Calle y número *</label>
                    <input value={form.calleNumero} onChange={e => set('calleNumero', e.target.value.toLowerCase().replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                      placeholder="Blvd. Insurgentes #456" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Ciudad *</label>
                    <CiudadCombobox placeholder="Ej. Tijuana, BC" value={form.ciudad} onChange={v => set('ciudad', v)} dark />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Número de unidades *</label>
                    <input type="number" min="1" value={form.flota} onChange={e => set('flota', e.target.value)}
                      placeholder="5" className={inputCls} />
                  </div>
                </div>
              </div>
            )}

            {/* ── PASO 2: Credenciales ── */}
            {paso === 2 && (
              <div className="space-y-4">
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-2">
                  <p className="text-sm font-semibold text-orange-300">Credenciales de acceso al portal</p>
                  <p className="text-xs text-orange-400 mt-0.5">Con estas credenciales podrás iniciar sesión una vez que tu cuenta sea aprobada.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-1">Correo electrónico de acceso *</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                    placeholder="tu@empresa.mx" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-1">Contraseña *</label>
                  <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                    placeholder="Mínimo 8 caracteres" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-1">Confirmar contraseña *</label>
                  <input type="password" value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)}
                    placeholder="Repite la contraseña" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Cód. de invitación <span className="text-gray-500 font-normal">(opcional)</span></label>
                    <input type="text" value={form.codigoInvitacion} onChange={e => set('codigoInvitacion', e.target.value.toUpperCase())}
                      placeholder="CHO-ABC123" maxLength={20} className={`${inputCls} font-mono`} />
                    <p className="text-xs text-gray-500 mt-1">Referido de alguien</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Cód. de acceso <span className="text-gray-500 font-normal">(opcional)</span></label>
                    <input type="text" value={form.codigoPromo} onChange={e => set('codigoPromo', e.target.value.toUpperCase().replace(/\s/g, ''))}
                      placeholder="LANZAMIENTO" maxLength={30} className={`${inputCls} font-mono`} />
                    <p className="text-xs text-gray-500 mt-1">Código de promoción</p>
                  </div>
                </div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input type="checkbox" checked={form.aceptaTerminos} onChange={e => set('aceptaTerminos', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-orange-500 flex-shrink-0" />
                  <span className="text-sm text-gray-400">
                    Acepto los <span onClick={e => { e.preventDefault(); setVerTerminos(true); }} className="text-orange-400 font-semibold underline cursor-pointer">términos y condiciones</span> y la <span onClick={e => { e.preventDefault(); setVerTerminos(true); }} className="text-orange-400 font-semibold underline cursor-pointer">política de privacidad</span> de Choyero Marketplace Logístico.
                  </span>
                </label>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400 flex items-center space-x-2">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Botones de navegación */}
            <div className="flex justify-between mt-8">
              {paso > 1 ? (
                <button onClick={() => { setPaso(p => p - 1); setError(''); }}
                  className="px-6 py-2.5 bg-white/5 border border-white/10 text-gray-300 rounded-xl hover:bg-white/10 font-semibold flex items-center space-x-2">
                  <ArrowLeft size={18} /><span>Atrás</span>
                </button>
              ) : <div />}
              {paso < 2 ? (
                <button onClick={() => { if (paso === 1 && !validarPaso1()) return; setPaso(p => p + 1); setError(''); }}
                  className="px-6 py-2.5 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-semibold flex items-center space-x-2 shadow-lg shadow-orange-500/20">
                  <span>Siguiente</span><ChevronRight size={18} />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={cargando}
                  className="px-8 py-2.5 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-semibold flex items-center space-x-2 disabled:opacity-50 shadow-lg shadow-orange-500/20">
                  {cargando ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle size={18} /><span>Enviar solicitud</span></>}
                </button>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">Choyero Marketplace Logístico © 2026</p>
      </div>
    </div>
  );
}

function RegistroCliente({ onVolver, onExito, modoReferidor = false }) {
  const [paso, setPaso] = useState(1);
  const [tipoCuenta, setTipoCuenta] = useState(modoReferidor ? 'referidor' : ''); // 'fletes' | 'empresa' | 'referidor'
  const [tipoPersona, setTipoPersona] = useState(modoReferidor ? 'fisica' : '');
  const refUrlRaw = new URLSearchParams(window.location.search).get('ref');
  const refUrl = refUrlRaw && /^[A-Z0-9a-z_-]{3,20}$/.test(refUrlRaw) ? refUrlRaw : '';
  const [form, setForm] = useState({
    nombre: '', curp: '', razonSocial: '', rfc: '', representante: '', telefono: '', calleNumero: '', ciudad: '',
    email: '', password: '', confirmPassword: '', aceptaTerminos: false, codigoInvitacion: refUrl.toUpperCase()
  });

  const seleccionarTipoCuenta = (tipo) => {
    setTipoCuenta(tipo);
    if (tipo === 'referidor') setTipoPersona('fisica');
    else if (tipo === 'empresa') setTipoPersona('moral');
    else if (tipo === 'fletes') setTipoPersona('fisica');
    else setTipoPersona('');
  };
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [verTerminos, setVerTerminos] = useState(false);

  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const validarPaso1 = () => {
    if (!tipoCuenta) { setError('Selecciona el tipo de cuenta'); return false; }
    if (!tipoPersona) { setError('Selecciona el tipo de persona'); return false; }
    if (tipoPersona === 'fisica') {
      if (!form.nombre || !form.telefono || !form.calleNumero || !form.ciudad) { setError('Completa todos los campos obligatorios'); return false; }
      if (tipoCuenta === 'fletes') {
        if (!form.rfc || form.rfc.length !== 13) { setError('El RFC debe tener exactamente 13 caracteres'); return false; }
        if (!validarDigitoVerificadorRFC(form.rfc)) { setError('El RFC no es válido. Verifica que esté escrito correctamente (dígito verificador incorrecto).'); return false; }
      } else {
        if (form.curp && form.curp.length !== 18) { setError('La CURP debe tener exactamente 18 caracteres'); return false; }
      }
      if (form.telefono.length !== 10) { setError('El teléfono debe tener exactamente 10 dígitos'); return false; }
    } else {
      if (!form.razonSocial || !form.rfc || !form.representante || !form.telefono || !form.calleNumero || !form.ciudad) { setError('Completa todos los campos obligatorios'); return false; }
      if (form.rfc.length !== 12) { setError('El RFC debe tener exactamente 12 caracteres'); return false; }
      if (!validarDigitoVerificadorRFC(form.rfc)) { setError('El RFC no es válido. Verifica que esté escrito correctamente (dígito verificador incorrecto).'); return false; }
      if (form.telefono.length !== 10) { setError('El teléfono debe tener exactamente 10 dígitos'); return false; }
    }
    setError(''); return true;
  };

  const validarPaso2 = () => {
    if (!form.email || !form.password) { setError('Ingresa tu correo y contraseña'); return false; }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return false; }
    if (form.password !== form.confirmPassword) { setError('Las contraseñas no coinciden'); return false; }
    if (!form.aceptaTerminos) { setError('Debes aceptar los términos y condiciones'); return false; }
    setError(''); return true;
  };

  const handleSubmit = async () => {
    if (!validarPaso2()) return;
    setCargando(true);
    try {
      const disponible = await apiCheckEmail(form.email).catch(() => true);
      if (!disponible) { setError('Este correo ya está registrado'); setCargando(false); return; }

      await apiRegistrarClienteCompleto(
        { tipoPersona, esReferidor: tipoCuenta === 'referidor', nombre: form.nombre, curp: form.curp, razonSocial: form.razonSocial, rfc: form.rfc, representante: form.representante, telefono: form.telefono, calleNumero: form.calleNumero, ciudad: form.ciudad },
        { email: form.email, password: form.password },
        form.codigoInvitacion || undefined
      );
      // Fallback local
      registrarCliente({
        datos: { tipoPersona, nombre: form.nombre, curp: form.curp, razonSocial: form.razonSocial, rfc: form.rfc, representante: form.representante, telefono: form.telefono, calleNumero: form.calleNumero, ciudad: form.ciudad },
        credenciales: { email: form.email, password: form.password }
      });
      phCapture('usuario_registro', { rol: 'cliente', tipoCuenta, ciudad: form.ciudad });
      setCargando(false);
      onExito(tipoCuenta);
    } catch (err) {
      setError(err.message || 'Error al registrar. Intenta de nuevo.');
      setCargando(false);
    }
  };

  const inputCls = 'w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-400 focus:outline-none text-white placeholder-gray-500 text-sm';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 flex items-center justify-center p-4">
      {verTerminos && <ModalTerminos onClose={() => setVerTerminos(false)} />}
      <div className="w-full max-w-lg">

        <div className="flex items-center justify-between mb-8">
          <button onClick={onVolver} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition">
            <ArrowLeft size={16} /> Volver al inicio
          </button>
          <div className="flex items-center gap-3">
            <img src="/logo-choyero.png" alt="Choyero" className="h-12 w-auto" />
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          {/* Progress */}
          <div className="px-8 pt-6 pb-4 border-b border-white/10">
            <h2 className="text-2xl font-bold text-white mb-1">{modoReferidor ? 'Registro de Referidor' : 'Registro de Cliente'}</h2>
            <p className="text-gray-400 text-sm mb-5">{modoReferidor ? 'Únete al Programa de Referidos Choyero!' : 'Crea tu cuenta y empieza a solicitar fletes'}</p>
            <div className="flex items-center space-x-2">
              {[1, 2].map(p => (
                <React.Fragment key={p}>
                  <div className={`flex items-center space-x-2 ${paso >= p ? 'text-blue-400' : 'text-gray-500'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${paso > p ? 'bg-green-500 text-white' : paso === p ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-400'}`}>
                      {paso > p ? <CheckCircle size={16} /> : p}
                    </div>
                    <span className="text-sm font-medium hidden sm:block">{p === 1 ? 'Datos personales' : 'Acceso'}</span>
                  </div>
                  {p < 2 && <div className={`flex-1 h-1 rounded-full ${paso > p ? 'bg-green-500' : 'bg-white/10'}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="p-8">
            {/* ── PASO 1: Tipo de persona + datos ── */}
            {paso === 1 && (
              <div className="space-y-5">
                {/* Selector de tipo de cuenta */}
                {modoReferidor ? (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center gap-3">
                    <User size={20} className="text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="text-orange-300 font-semibold text-sm">Registro como Referidor</p>
                      <p className="text-gray-400 text-xs mt-0.5">Tu cuenta tendrá acceso al Programa de Referidos de Choyero</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-3">¿Qué tipo de cuenta deseas? *</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={() => seleccionarTipoCuenta('fletes')}
                        className={`border-2 rounded-xl p-4 text-left transition-all ${tipoCuenta === 'fletes' ? 'border-green-500 bg-green-500/10' : 'border-white/10 bg-white/5 hover:border-green-400 hover:bg-white/10'}`}>
                        <Package size={22} className={`mb-2 ${tipoCuenta === 'fletes' ? 'text-green-400' : 'text-gray-400'}`} />
                        <p className={`font-bold text-sm ${tipoCuenta === 'fletes' ? 'text-green-300' : 'text-white'}`}>Persona Física</p>
                        <p className="text-xs text-gray-500 mt-0.5">Particular, autónomo o profesional independiente</p>
                      </button>
                      <button type="button" onClick={() => seleccionarTipoCuenta('empresa')}
                        className={`border-2 rounded-xl p-4 text-left transition-all ${tipoCuenta === 'empresa' ? 'border-blue-500 bg-blue-500/10' : 'border-white/10 bg-white/5 hover:border-blue-400 hover:bg-white/10'}`}>
                        <Building2 size={22} className={`mb-2 ${tipoCuenta === 'empresa' ? 'text-blue-400' : 'text-gray-400'}`} />
                        <p className={`font-bold text-sm ${tipoCuenta === 'empresa' ? 'text-blue-300' : 'text-white'}`}>Persona Moral</p>
                        <p className="text-xs text-gray-500 mt-0.5">Empresa, SA de CV, SAPI u otra razón social</p>
                      </button>
                    </div>
                  </div>
                )}

                {tipoPersona === 'fisica' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Nombre completo *</label>
                      <input value={form.nombre} onChange={e => set('nombre', e.target.value.replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                        placeholder="María López Hernández" className={inputCls} />
                    </div>
                    {tipoCuenta === 'fletes' ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-1">RFC * <span className="text-gray-500 font-normal">(13 caracteres)</span></label>
                        <input value={form.rfc}
                          onChange={e => set('rfc', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 13))}
                          placeholder="LOPM850615MB1" maxLength={13}
                          className={`${inputCls} ${form.rfc.length > 0 && form.rfc.length !== 13 ? '!border-red-500' : ''}`} />
                        {form.rfc.length > 0 && <p className="text-xs mt-1 text-right" style={{color: form.rfc.length === 13 ? '#4ade80' : '#f87171'}}>{form.rfc.length}/13</p>}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-1">CURP <span className="text-gray-500 font-normal">(opcional · 18 caracteres)</span></label>
                        <input value={form.curp}
                          onChange={e => set('curp', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 18))}
                          placeholder="LOPM850615MBCPRR09" maxLength={18}
                          className={`${inputCls} ${form.curp.length > 0 && form.curp.length !== 18 ? '!border-red-500' : ''}`} />
                        {form.curp.length > 0 && <p className="text-xs mt-1 text-right" style={{color: form.curp.length === 18 ? '#4ade80' : '#f87171'}}>{form.curp.length}/18</p>}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Teléfono * <span className="text-gray-500 font-normal">(10 dígitos)</span></label>
                      <input value={form.telefono}
                        onChange={e => set('telefono', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="6641234567" maxLength={10} inputMode="numeric"
                        className={`${inputCls} ${form.telefono.length > 0 && form.telefono.length !== 10 ? '!border-red-500' : ''}`} />
                      {form.telefono.length > 0 && <p className="text-xs mt-1 text-right" style={{color: form.telefono.length === 10 ? '#4ade80' : '#f87171'}}>{form.telefono.length}/10</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Calle y número *</label>
                      <input value={form.calleNumero} onChange={e => set('calleNumero', e.target.value.toLowerCase().replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                        placeholder="Ej. Av. Revolución #123" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Ciudad *</label>
                      <CiudadCombobox placeholder="Ej. Tijuana, BC" value={form.ciudad} onChange={v => set('ciudad', v)} dark />
                    </div>
                  </div>
                )}

                {tipoPersona === 'moral' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Razón social *</label>
                      <input value={form.razonSocial} onChange={e => set('razonSocial', e.target.value.replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                        placeholder="Distribuidora del Pacífico SA de CV" className={inputCls} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-1">RFC * <span className="text-gray-500 font-normal">(12 chars)</span></label>
                        <input value={form.rfc}
                          onChange={e => set('rfc', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 12))}
                          placeholder="DPA850615XXX" maxLength={12}
                          className={`${inputCls} ${form.rfc.length > 0 && form.rfc.length !== 12 ? '!border-red-500' : ''}`} />
                        {form.rfc.length > 0 && <p className="text-xs mt-1 text-right" style={{color: form.rfc.length === 12 ? '#4ade80' : '#f87171'}}>{form.rfc.length}/12</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-1">Teléfono * <span className="text-gray-500 font-normal">(10 díg.)</span></label>
                        <input value={form.telefono}
                          onChange={e => set('telefono', e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="6641234567" maxLength={10} inputMode="numeric"
                          className={`${inputCls} ${form.telefono.length > 0 && form.telefono.length !== 10 ? '!border-red-500' : ''}`} />
                        {form.telefono.length > 0 && <p className="text-xs mt-1 text-right" style={{color: form.telefono.length === 10 ? '#4ade80' : '#f87171'}}>{form.telefono.length}/10</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Representante legal *</label>
                      <input value={form.representante} onChange={e => set('representante', e.target.value)}
                        placeholder="Lic. Juan Pérez" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Calle y número *</label>
                      <input value={form.calleNumero} onChange={e => set('calleNumero', e.target.value.toLowerCase().replace(/(?:^|\s)\S/g, c => c.toUpperCase()))}
                        placeholder="Ej. Blvd. Agua Caliente #456" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">Ciudad *</label>
                      <CiudadCombobox placeholder="Ej. Tijuana, BC" value={form.ciudad} onChange={v => set('ciudad', v)} dark />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── PASO 2: Credenciales ── */}
            {paso === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-1">Correo electrónico *</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                    placeholder="tu@correo.mx" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-1">Contraseña * <span className="text-gray-500 font-normal">(mín. 8 caracteres)</span></label>
                  <div className="relative">
                    <input type={mostrarPassword ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                      placeholder="••••••••" className={`${inputCls} pr-10`} />
                    <button type="button" onClick={() => setMostrarPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                      {mostrarPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-1">Confirmar contraseña *</label>
                  <input type="password" value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)}
                    placeholder="••••••••"
                    className={`${inputCls} ${form.confirmPassword && form.confirmPassword !== form.password ? '!border-red-500' : ''}`} />
                  {form.confirmPassword && form.confirmPassword !== form.password && (
                    <p className="text-xs text-red-400 mt-1">Las contraseñas no coinciden</p>
                  )}
                </div>
                {tipoPersona && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Código de invitación <span className="text-gray-500 font-normal">(opcional)</span></label>
                    <input type="text" value={form.codigoInvitacion} onChange={e => set('codigoInvitacion', e.target.value.toUpperCase())}
                      placeholder="Ej: CHO-ABC123" maxLength={20} className={`${inputCls} font-mono`} />
                    <p className="text-xs text-gray-500 mt-1">
                      {tipoPersona === 'moral'
                        ? 'Si alguien te recomendó Choyero, ingresa su código para que reciba una comisión.'
                        : 'Si alguien te invitó, ingresa su código para unirte a su red de referidos.'}
                    </p>
                  </div>
                )}
                <label className="flex items-start space-x-3 cursor-pointer mt-2">
                  <input type="checkbox" checked={form.aceptaTerminos} onChange={e => set('aceptaTerminos', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-blue-500" />
                  <span className="text-sm text-gray-400">Acepto los <span onClick={e => { e.preventDefault(); setVerTerminos(true); }} className="text-blue-400 underline cursor-pointer">términos y condiciones</span> y la <span onClick={e => { e.preventDefault(); setVerTerminos(true); }} className="text-blue-400 underline cursor-pointer">política de privacidad</span> de Choyero Marketplace Logístico.</span>
                </label>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400 flex items-center space-x-2">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Navegación */}
            <div className="flex justify-between mt-8">
              {paso > 1 ? (
                <button onClick={() => { setPaso(p => p - 1); setError(''); }}
                  className="px-6 py-2.5 bg-white/5 border border-white/10 text-gray-300 rounded-xl hover:bg-white/10 font-semibold flex items-center space-x-2">
                  <ArrowLeft size={18} /><span>Atrás</span>
                </button>
              ) : <div />}
              {paso < 2 ? (
                <button onClick={() => { if (validarPaso1()) { setPaso(2); setError(''); } }}
                  className="px-6 py-2.5 bg-blue-500 hover:bg-blue-400 text-white rounded-xl font-semibold flex items-center space-x-2 shadow-lg shadow-blue-500/20">
                  <span>Siguiente</span><ChevronRight size={18} />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={cargando}
                  className="px-8 py-2.5 bg-blue-500 hover:bg-blue-400 text-white rounded-xl font-semibold flex items-center space-x-2 disabled:opacity-50 shadow-lg shadow-blue-500/20">
                  {cargando ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle size={18} /><span>Crear cuenta</span></>}
                </button>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">Choyero Marketplace Logístico © 2026</p>
      </div>
    </div>
  );
}

function RegistroExitoso({ onVolver, tipo }) {
  const esTransportista = tipo === 'transportista';
  const esReferidor = tipo === 'referidor';
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 flex items-center justify-center p-4">
      <div className="bg-white/5 border border-white/10 rounded-2xl max-w-md w-full p-10 text-center">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={44} className="text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">¡Cuenta creada!</h2>
        <p className="text-gray-400 mb-6">
          {esTransportista
            ? 'Tu empresa fue registrada correctamente. Ya puedes iniciar sesión para subir tu documentación.'
            : esReferidor
              ? 'Tu cuenta de referidor fue creada correctamente. Ya puedes iniciar sesión y empezar a ganar comisiones refiriendo clientes.'
              : 'Tu cuenta fue creada correctamente. Ya puedes iniciar sesión y empezar a solicitar fletes.'}
        </p>
        {esTransportista && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-orange-300 mb-1">¿Qué sigue?</p>
            <ul className="text-sm text-orange-400 space-y-1">
              <li>• Inicia sesión con las credenciales que registraste</li>
              <li>• Sube los documentos requeridos desde tu portal</li>
              <li>• El administrador revisará tu solicitud y activará tu acceso completo</li>
            </ul>
          </div>
        )}
        {esReferidor && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-orange-300 mb-1">¿Qué sigue?</p>
            <ul className="text-sm text-orange-400 space-y-1">
              <li>• Inicia sesión con las credenciales que registraste</li>
              <li>• Obtén tu enlace de referido desde tu portal</li>
              <li>• Compártelo y gana comisiones por cada cliente que se registre</li>
            </ul>
          </div>
        )}
        <button onClick={onVolver}
          className="w-full py-3 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-bold transition flex items-center justify-center space-x-2 shadow-lg shadow-orange-500/20">
          <ArrowLeft size={18} />
          <span>{esTransportista ? 'Ir a iniciar sesión' : 'Ir ahora'}</span>
        </button>
      </div>
    </div>
  );
}

export default function Login({ onLogin, vistaInicial, onVolver, resetTokenInicial }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [vistaRegistro, setVistaRegistro] = useState(vistaInicial === 'transportista');
  const [vistaRegistroCliente, setVistaRegistroCliente] = useState(vistaInicial === 'cliente');
  const [vistaRegistroReferidor, setVistaRegistroReferidor] = useState(vistaInicial === 'referidor');
  const [mostrarElegir, setMostrarElegir] = useState(vistaInicial === 'elegir');
  const [registroExitoso, setRegistroExitoso] = useState(null); // 'transportista' | 'cliente' | null

  // ── Recuperación de contraseña ──
  // Si llegó un token desde el enlace del email, abrir directamente en paso 2
  const [modalRecuperar, setModalRecuperar] = useState(!!resetTokenInicial);
  const [recuperarPaso, setRecuperarPaso] = useState(resetTokenInicial ? 2 : 1);
  const [recuperarEmail, setRecuperarEmail] = useState('');
  const [recuperarToken, setRecuperarToken] = useState(resetTokenInicial || '');
  const [recuperarNueva, setRecuperarNueva] = useState('');
  const [recuperarConfirm, setRecuperarConfirm] = useState('');
  const [recuperarMsg, setRecuperarMsg] = useState(null);
  const [recuperarCargando, setRecuperarCargando] = useState(false);
  const [mostrarNuevaPass, setMostrarNuevaPass] = useState(false);

  const abrirRecuperar = () => {
    setRecuperarPaso(1); setRecuperarEmail(''); setRecuperarToken('');
    setRecuperarNueva(''); setRecuperarConfirm(''); setRecuperarMsg(null);
    setModalRecuperar(true);
  };

  const handleEnviarRecuperacion = async () => {
    if (!recuperarEmail.trim()) return setRecuperarMsg({ ok: false, texto: 'Ingresa tu correo electrónico.' });
    setRecuperarCargando(true);
    setRecuperarMsg(null);
    try {
      const res = await apiForgotPassword(recuperarEmail.trim().toLowerCase());
      if (res.dev && res.token) {
        setRecuperarToken(res.token);
        setRecuperarMsg({ ok: true, texto: 'Modo desarrollo: token copiado automáticamente. Escribe tu nueva contraseña.', dev: true });
        setRecuperarPaso(2);
      } else {
        setRecuperarMsg({ ok: true, texto: res.mensaje || 'Si el correo está registrado recibirás un enlace. Revisa también tu carpeta de spam.' });
        setRecuperarPaso(2);
      }
    } catch (e) {
      // Error 503 = SMTP no configurado, mostrar mensaje claro sin avanzar a paso 2
      setRecuperarMsg({ ok: false, texto: e.message || 'Error al enviar solicitud' });
    } finally {
      setRecuperarCargando(false);
    }
  };

  const handleRestablecerPassword = async () => {
    if (!recuperarToken.trim()) return setRecuperarMsg({ ok: false, texto: 'Ingresa el código de recuperación.' });
    if (recuperarNueva.length < 8) return setRecuperarMsg({ ok: false, texto: 'La contraseña debe tener al menos 8 caracteres.' });
    if (recuperarNueva !== recuperarConfirm) return setRecuperarMsg({ ok: false, texto: 'Las contraseñas no coinciden.' });
    setRecuperarCargando(true);
    setRecuperarMsg(null);
    try {
      const res = await apiResetPassword(recuperarToken.trim(), recuperarNueva);
      setRecuperarMsg({ ok: true, texto: res.mensaje || '¡Contraseña actualizada! Ya puedes iniciar sesión.' });
      setTimeout(() => { setModalRecuperar(false); setEmail(recuperarEmail); }, 2000);
    } catch (e) {
      setRecuperarMsg({ ok: false, texto: e.message || 'Token inválido o expirado.' });
    } finally {
      setRecuperarCargando(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      const usuario = await apiLogin(email, password);
      onLogin(usuario);
    } catch (err) {
      setError(err.message || 'Correo o contraseña incorrectos');
    } finally {
      setCargando(false);
    }
  };

  const loginRapido = async (rolDemo) => {
    setCargando(true);
    setError('');
    try {
      const usuario = await apiLogin(rolDemo.email, rolDemo.password);
      onLogin(usuario);
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setCargando(false);
    }
  };

  const handleReset = () => {
    resetStore();
    setResetConfirm(false);
    setError('');
    alert('✅ Datos de demo restaurados correctamente');
  };

  if (registroExitoso) return <RegistroExitoso tipo={registroExitoso} onVolver={() => { setVistaRegistro(false); setVistaRegistroCliente(false); setVistaRegistroReferidor(false); setRegistroExitoso(null); setMostrarElegir(false); }} />;
  if (vistaRegistro) return <RegistroTransportista onVolver={vistaInicial === 'transportista' && onVolver ? onVolver : () => setVistaRegistro(false)} onExito={() => setRegistroExitoso('transportista')} />;
  if (vistaRegistroCliente) return <RegistroCliente onVolver={vistaInicial === 'cliente' && onVolver ? onVolver : () => setVistaRegistroCliente(false)} onExito={(tc) => setRegistroExitoso(tc === 'referidor' ? 'referidor' : 'cliente')} />;
  if (vistaRegistroReferidor) return <RegistroCliente modoReferidor={true} onVolver={vistaInicial === 'referidor' && onVolver ? onVolver : () => setVistaRegistroReferidor(false)} onExito={() => setRegistroExitoso('referidor')} />;

  // Pantalla de selección de tipo de cuenta (cuando viene de "Registrarme" en la landing)
  if (mostrarElegir) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-10">
          {onVolver && (
            <button onClick={onVolver} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition">
              <ArrowLeft size={16} /> Volver al inicio
            </button>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <img src="/logo-choyero.png" alt="Choyero" className="h-12 w-auto" />
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">¿Cómo quieres registrarte en Choyero?</h2>
          <p className="text-gray-400 text-sm">Selecciona el tipo de cuenta que necesitas</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <button
            onClick={() => setVistaRegistroCliente(true)}
            className="group bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-blue-400 rounded-2xl p-6 text-left transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <Package size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-lg">Soy cliente</p>
                <p className="text-gray-400 text-sm mt-0.5">Quiero enviar mercancía y contratar fletes</p>
              </div>
              <ChevronRight size={20} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
            </div>
          </button>

          <button
            onClick={() => setVistaRegistro(true)}
            className="group bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-orange-400 rounded-2xl p-6 text-left transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <Truck size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-lg">Soy transportista</p>
                <p className="text-gray-400 text-sm mt-0.5">Tengo una empresa de transporte y quiero ofrecer servicios</p>
              </div>
              <ChevronRight size={20} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
            </div>
          </button>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          ¿Ya tienes cuenta?{' '}
          <button
            onClick={() => { setVistaRegistro(false); setVistaRegistroCliente(false); setMostrarElegir(false); }}
            className="text-orange-400 hover:text-orange-300 font-medium transition"
          >
            Inicia sesión
          </button>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          {onVolver && (
            <button onClick={onVolver} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition">
              <ArrowLeft size={16} /> Volver al inicio
            </button>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <img src="/logo-choyero.png" alt="Choyero" className="h-12 w-auto" />
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Iniciar sesión</h2>
          <p className="text-gray-400 text-sm">Ingresa tus credenciales</p>
        </div>

        {/* Panel login */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Correo electrónico o teléfono</label>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="text"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@correo.mx o 6151234567"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 text-sm"
                required
              />

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Contraseña</label>
                <div className="relative">
                  <input
                    type={mostrarPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 text-sm pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarPassword(!mostrarPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  >
                    {mostrarPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={abrirRecuperar} className="text-xs text-orange-400 hover:text-orange-300 transition">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-400">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={cargando}
                className="w-full py-3 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-bold transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20"
              >
                {cargando ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn size={18} />
                    <span>Entrar</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Registro */}
          <div className="pt-4 space-y-3">
            <p className="text-sm text-gray-500 text-center">¿Aún no tienes cuenta?</p>
            <button
              type="button"
              onClick={() => setVistaRegistroCliente(true)}
              className="group w-full bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-blue-400 rounded-2xl p-4 text-left transition-all flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <User size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Soy cliente</p>
                <p className="text-gray-400 text-xs mt-0.5">Quiero enviar mercancía y contratar fletes</p>
              </div>
              <ChevronRight size={18} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
            </button>
            <button
              type="button"
              onClick={() => setVistaRegistro(true)}
              className="group w-full bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-orange-400 rounded-2xl p-4 text-left transition-all flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <Truck size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Soy transportista</p>
                <p className="text-gray-400 text-xs mt-0.5">Tengo una empresa de transporte y quiero ofrecer servicios</p>
              </div>
              <ChevronRight size={18} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-8">
          Choyero Marketplace Logístico © 2026
        </p>
      </div>

      {/* ── Modal recuperación de contraseña ── */}
      {modalRecuperar && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-lg">
                {recuperarPaso === 1 ? 'Recuperar contraseña' : 'Crear nueva contraseña'}
              </h3>
              <button onClick={() => setModalRecuperar(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {recuperarPaso === 1 ? (
                <>
                  <p className="text-sm text-gray-500">Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Correo electrónico</label>
                    <input
                      type="email"
                      value={recuperarEmail}
                      onChange={e => setRecuperarEmail(e.target.value)}
                      placeholder="tu@correo.mx"
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      onKeyDown={e => e.key === 'Enter' && handleEnviarRecuperacion()}
                    />
                  </div>
                </>
              ) : (
                <>
                  {recuperarMsg?.dev && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-xs text-yellow-800">
                      <strong>Modo desarrollo:</strong> El token fue generado. Cópialo del campo de abajo o revisa la consola del backend.
                    </div>
                  )}
                  {!recuperarMsg?.dev && (
                    <p className="text-sm text-gray-500">Revisa tu correo y pega el código de recuperación. Luego elige una nueva contraseña.</p>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Código de recuperación</label>
                    <input
                      type="text"
                      value={recuperarToken}
                      onChange={e => setRecuperarToken(e.target.value)}
                      placeholder="Pega aquí el código recibido"
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nueva contraseña</label>
                    <div className="relative">
                      <input
                        type={mostrarNuevaPass ? 'text' : 'password'}
                        value={recuperarNueva}
                        onChange={e => setRecuperarNueva(e.target.value)}
                        placeholder="Mínimo 8 caracteres"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                      <button type="button" onClick={() => setMostrarNuevaPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {mostrarNuevaPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Confirmar contraseña</label>
                    <input
                      type="password"
                      value={recuperarConfirm}
                      onChange={e => setRecuperarConfirm(e.target.value)}
                      placeholder="Repite tu nueva contraseña"
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                </>
              )}

              {recuperarMsg && (
                <p className={`text-sm font-medium rounded-lg p-3 ${recuperarMsg.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {recuperarMsg.texto}
                </p>
              )}
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button onClick={() => setModalRecuperar(false)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">
                Cancelar
              </button>
              {recuperarPaso === 1 ? (
                <button
                  onClick={handleEnviarRecuperacion}
                  disabled={recuperarCargando || !recuperarEmail.trim()}
                  className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition flex items-center justify-center gap-2"
                >
                  {recuperarCargando ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Enviar enlace'}
                </button>
              ) : (
                <button
                  onClick={handleRestablecerPassword}
                  disabled={recuperarCargando || !recuperarToken || !recuperarNueva || recuperarNueva !== recuperarConfirm}
                  className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition flex items-center justify-center gap-2"
                >
                  {recuperarCargando ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Actualizar contraseña'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
